import {
  getHttpDeleteOptions,
  getHttpGetOptions,
  getHttpPostOptions,
  getHttpPutOptions
} from '@neslotech/utils';

import { Mandate } from '../../interfaces/client/mandate.interface';

import { getAuthHeaders } from '../auth.util';

import { API_HOST, RequestOptions } from './index';

const getMandatesEndpoint = (): string => `${API_HOST}/mandates`;
const getMandateEndpoint = (id: number | string): string => `${API_HOST}/mandates/${id}`;

export const getMandatesRequest = (): RequestOptions => [
  getMandatesEndpoint(),
  getHttpGetOptions(getAuthHeaders())
];

/** v3: PUT /mandates/:id with the fields the redesign edits inline. */
export const getUpdateMandateRequest = (
  id: number | string,
  payload: Partial<Mandate>
): RequestOptions => [
  getMandateEndpoint(id),
  getHttpPutOptions({ mandate: payload }, getAuthHeaders())
];

/**
 * v3: DELETE /mandates/:id — top-level delete.
 *
 * Note this is NOT the same as the client-scoped
 * DELETE /clients/:id/mandates/:mandate_id used by the legacy screens; the v3
 * lists only know the mandate id, not its client.
 */
export const getDeleteMandateRequest = (id: number | string): RequestOptions => [
  getMandateEndpoint(id),
  getHttpDeleteOptions(getAuthHeaders())
];

/** v3: POST /mandates — top-level create.  `client_id` is part of the body. */
export const getCreateMandateRequest = (payload: Partial<Mandate>): RequestOptions => [
  getMandatesEndpoint(),
  getHttpPostOptions({ mandate: payload }, getAuthHeaders())
];

/**
 * v3: GET /mandates/:id/versions — paper_trail version history rendered on
 * the MandateDetail Activity tab.  Returns an array of
 * { id, event, created_at, actor_id, actor, changes: [{field, from, to}] }.
 */
export const getMandateVersionsRequest = (id: number | string): RequestOptions => [
  `${getMandateEndpoint(id)}/versions`,
  getHttpGetOptions(getAuthHeaders())
];

/**
 * v3: GET /mandates/:id/documents — Active Storage attachments rendered on
 * the Documents tab.  Returns { id, blob_id, name, content_type,
 * byte_size, created_at, url }[].
 */
export const getMandateDocumentsRequest = (id: number | string): RequestOptions => [
  `${getMandateEndpoint(id)}/documents`,
  getHttpGetOptions(getAuthHeaders())
];

/**
 * v3: POST /mandates/:id/documents with a base64 data-URL `data` field.
 * Server reuses ImageHelper.create_attachment_for_image to decode + attach.
 */
export const getAttachMandateDocumentRequest = (
  id: number | string,
  payload: { name: string; data: string }
): RequestOptions => [
  `${getMandateEndpoint(id)}/documents`,
  getHttpPostOptions({ document: payload }, getAuthHeaders())
];

/** v3: DELETE /mandates/:id/documents/:attachment_id (purges the blob). */
export const getDetachMandateDocumentRequest = (
  id: number | string,
  attachmentId: number | string
): RequestOptions => [
  `${getMandateEndpoint(id)}/documents/${attachmentId}`,
  getHttpDeleteOptions(getAuthHeaders())
];
